/**
 * WPS-137: the New Registration wizard can pin an existing property/host record
 * (`selectedExistingHostUuid`) - via the duplicate-address "continue with this
 * property" decision, the by-consumer existing-host picker, or a property
 * deep-link prefill. That pin is tied to ONE property address. If the operator
 * then changes the property address (instead of cancelling and starting over),
 * the pin is stale: sending it registers the warranty against the PREVIOUS
 * address' property regardless of the address now on screen, because the
 * backend trusts `existing_host_uuid` unconditionally over the submitted
 * `host_data`. The reported symptom was entering address A, changing to B, and
 * the warranty landing on A.
 *
 * These pure helpers decide, from the address key the host was pinned to and
 * the address key currently on the form, whether the pin is still valid. The
 * key is produced by `addressDuplicateKey` - `line1|postcode|city|country`,
 * trimmed + lower-cased. An "empty" key (no non-empty segment) means the
 * address is not yet known: the host template may still be loading, or the
 * operator is mid-edit. We must neither pin to nor reset from an empty key, or
 * a property prefill would be dropped on a slow load.
 */

export interface PinnedHostState {
  selectedExistingHostUuid: string | null;
  /**
   * The address key (see `addressDuplicateKey`) the pinned host is tied to.
   * `null` means "pinned but not yet observed" - captured on the first render
   * where the address is meaningful (covers prefill and pre-fix cached drafts).
   * `undefined` is accepted and treated identically to `null` for a legacy
   * cached draft written before this field existed.
   */
  selectedExistingHostAddressKey: string | null | undefined;
}

/** A key is meaningful once it carries at least one non-empty address segment. */
export function isMeaningfulAddressKey(
  key: string | null | undefined,
): boolean {
  if (!key) return false;
  return key.split("|").some((part) => part.trim().length > 0);
}

/**
 * Given the current pin and the address key now on the form, return the state
 * patch to apply, or `null` when nothing should change. Two transitions:
 *  - capture: a host is pinned but we have not yet recorded the address it is
 *    tied to (prefill / a pre-fix cached draft). Record the current key, but
 *    only once the address is meaningful.
 *  - reset: the recorded key diverges from a meaningful current key -> the
 *    property address was changed, so drop the stale pin.
 */
export function reconcilePinnedHost(
  pinned: PinnedHostState,
  currentAddressKey: string,
): PinnedHostState | null {
  if (!pinned.selectedExistingHostUuid) return null;
  // Ignore transient empty keys (template still loading, address mid-edit): do
  // not capture from them and do not treat them as an address change.
  if (!isMeaningfulAddressKey(currentAddressKey)) return null;
  // `== null` (not `===`) so a legacy cached draft whose field is `undefined`
  // is captured rather than treated as a divergence and false-reset.
  if (pinned.selectedExistingHostAddressKey == null) {
    return {
      selectedExistingHostUuid: pinned.selectedExistingHostUuid,
      selectedExistingHostAddressKey: currentAddressKey,
    };
  }
  if (pinned.selectedExistingHostAddressKey !== currentAddressKey) {
    return {
      selectedExistingHostUuid: null,
      selectedExistingHostAddressKey: null,
    };
  }
  return null;
}

/**
 * The existing-host UUID that is safe to submit: the pin is dropped when its
 * recorded address key diverges from the meaningful address currently on the
 * form. A `null` recorded key (not yet captured) is trusted, since the
 * reconcile pass captures it before submit and any address change would have
 * reset it. This is defence-in-depth behind `reconcilePinnedHost` so a stale
 * UUID can never reach the backend even if the reset pass has not run.
 */
export function resolveSubmittableExistingHostUuid(
  pinned: PinnedHostState,
  currentAddressKey: string,
): string | null {
  if (!pinned.selectedExistingHostUuid) return null;
  // `!= null` (not `!==`) so an `undefined` recorded key (legacy cached draft)
  // is trusted rather than dropped.
  if (
    pinned.selectedExistingHostAddressKey != null &&
    isMeaningfulAddressKey(currentAddressKey) &&
    pinned.selectedExistingHostAddressKey !== currentAddressKey
  ) {
    return null;
  }
  return pinned.selectedExistingHostUuid;
}
